import React, { useEffect, useMemo, useState, useRef } from "react";
import GenerateExcel from "../../../../components/GenerateExcel";
import SearchableSelect from "../../../../comman/SearchableSelect";
import { formatAmount, getAllMonths, getAllYears } from "../../../../services/appService";
import { useDispatch, useSelector } from "react-redux";
import { getMonthlyGrossProfitCandidates, loadMonthlyGrossProfitCandidates } from "../../../store/reports";
import GeneratePdf from "../../../../components/GeneratePdf";

const GrossProfitCandidates = () => {
    const years = getAllYears();
    const months = getAllMonths();

    const candidateEntity = [
        { id: 1, name: "C2C Basis" },
        { id: 2, name: "W2 Basis" },
        { id: 3, name: "1099 Basis" },
    ];

    const yearOptions = [
        { value: "", label: "Year" },
        ...years.map((y) => ({ value: String(y.name), label: String(y.name) })),
    ];

    const monthOptions = [
        { value: "", label: "Month" },
        ...months.map((m) => ({ value: String(m.id), label: m.name })),
    ];

    const entityOptions = [
        { value: "", label: "All Entities" },
        ...candidateEntity.map((item) => ({ value: item.name, label: item.name })),
    ];

    const [pdfShowHide, setPdfShowHide] = useState(false);
    const [excelShowHide, setExcelShowHide] = useState(false);
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);

    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [selectedEntity, setSelectedEntity] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // Live search and scroll states
    const [tableSearchTerm, setTableSearchTerm] = useState("");
    const [tableWidth, setTableWidth] = useState(0);
    const [isScrollable, setIsScrollable] = useState(false);
    const topScrollRef = useRef(null);
    const tableWrapperRef = useRef(null);

    const monthlyGrossProfitSelector = useSelector(getMonthlyGrossProfitCandidates);
    const [monthlyGrossProfit, setMonthlyGrossProfit] = useState([]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (hasFetched) {
            setMonthlyGrossProfit(monthlyGrossProfitSelector || []);
        }
    }, [monthlyGrossProfitSelector, hasFetched]);

    // Only settle loading when fresh API data arrives (not when hasFetched flips true)
    useEffect(() => {
        setIsLoading(false);
    }, [monthlyGrossProfitSelector]);

    const handleYearChange = (value) => {
        setYear(value);
        setMonthlyGrossProfit([]);
        setTableSearchTerm("");
        setHasFetched(false);
    };

    const handleMonthChange = (value) => {
        setMonth(value);
        setMonthlyGrossProfit([]);
        setTableSearchTerm("");
        setHasFetched(false);
    };

    const GenerateGrossProfitRpt = () => {
        if (!year || !month) return;
        setIsLoading(true);
        setHasFetched(true);
        dispatch(loadMonthlyGrossProfitCandidates(year, month));
        setTableSearchTerm("");
    };

    // Filter Data by Entity and Search Term
    const filteredReport = useMemo(() => {
        if (!monthlyGrossProfit) return [];
        return monthlyGrossProfit.filter((item) => {
            // Filter by entity type
            if (selectedEntity !== "" && item.candidateType !== selectedEntity) {
                return false;
            }

            // Filter by search term
            const term = tableSearchTerm.toLowerCase();
            if (!term) return true;

            const name = `${item.candidateName || ""} ${item.candidateLastName || ""}`.toLowerCase();
            const candId = String(item.candidateId || "").toLowerCase();
            const type = (item.candidateType || "").toLowerCase();
            const sub = (item.subcontractor || "").toLowerCase();
            const rec = (item.recruiter || "").toLowerCase();

            return name.includes(term) ||
                   candId.includes(term) ||
                   type.includes(term) ||
                   sub.includes(term) ||
                   rec.includes(term);
        });
    }, [monthlyGrossProfit, selectedEntity, tableSearchTerm]);

    // Totals Calculation
    const totals = useMemo(() => {
        return filteredReport.reduce(
            (acc, item) => {
                acc.invoice += parseFloat(item.agencyAmount) || 0;
                acc.received += parseFloat(item.amountReceived) || 0;
                acc.candidate += parseFloat(item.candidateAmount) || 0;
                acc.subcontractor += parseFloat(item.subonctractorAmount) || 0; // original typo preserved
                acc.recruiter += parseFloat(item.recruiterAmount) || 0;
                acc.gross += parseFloat(item.grossProfit) || 0;
                return acc;
            },
            {
                invoice: 0,
                received: 0,
                candidate: 0,
                subcontractor: 0,
                recruiter: 0,
                gross: 0,
            }
        );
    }, [filteredReport]);

    // KPI Metrics values
    const getKpiStats = () => {
        const totalPayables = totals.candidate + totals.subcontractor + totals.recruiter;
        return {
            totalInvoicedStr: "$" + formatAmount(totals.invoice),
            totalReceivedStr: "$" + formatAmount(totals.received),
            totalPayablesStr: "$" + formatAmount(totalPayables),
            totalGrossProfitStr: "$" + formatAmount(totals.gross),
        };
    };

    const { totalInvoicedStr, totalReceivedStr, totalPayablesStr, totalGrossProfitStr } = getKpiStats();

    // Totals table row
    const getTotalRow = () => (
        <tr className="ach-pay-total-row">
            <td colSpan={5} className="text-right ach-pay-total-label">Total</td>
            <td className="text-right ach-pay-total-value">{"$" + formatAmount(totals.invoice)}</td>
            <td className="text-right ach-pay-total-value">{"$" + formatAmount(totals.received)}</td>
            <td className="text-right ach-pay-total-value">{"$" + formatAmount(totals.candidate)}</td>
            <td className="text-right ach-pay-total-value">{"$" + formatAmount(totals.subcontractor)}</td>
            <td className="text-right ach-pay-total-value">{"$" + formatAmount(totals.recruiter)}</td>
            <td className="text-right ach-pay-total-value">
                {"$" + formatAmount(totals.gross)}
            </td>
        </tr>
    );

    // Generate PDF data
    const generateGrossProfitPdf = () => {
        const headerList = [
            [
                "#",
                "Candidate",
                "Entity",
                "Subcontractor",
                "Recruiter",
                "Invoiced",
                "Received",
                "Payable Candidate",
                "Payable Subcontractor",
                "Payable Recruiter",
                "Gross Profit",
            ],
        ];

        const rowList = filteredReport.map((item) => [
            item.candidateId,
            `${item.candidateName} ${item.candidateLastName || ""}`.trim(),
            item.candidateType,
            item.subcontractor,
            item.recruiter,
            "$" + formatAmount(item.agencyAmount),
            "$" + formatAmount(item.amountReceived),
            "$" + formatAmount(item.candidateAmount),
            "$" + formatAmount(item.subonctractorAmount),
            "$" + formatAmount(item.recruiterAmount),
            "$" + formatAmount(item.grossProfit),
        ]);

        // add totals row
        rowList.push([
            "Total",
            "",
            "",
            "",
            "",
            "$" + formatAmount(totals.invoice),
            "$" + formatAmount(totals.received),
            "$" + formatAmount(totals.candidate),
            "$" + formatAmount(totals.subcontractor),
            "$" + formatAmount(totals.recruiter),
            "$" + formatAmount(totals.gross),
        ]);

        setHeaders(headerList);
        setRows(rowList);
        setPdfShowHide(true);
    }

    const generateGrossProfitExcel = () => {
        const headerList = [
            [
                "#",
                "Candidate",
                "Entity",
                "Subcontractor",
                "Recruiter",
                "Invoiced",
                "Received",
                "Payable Candidate",
                "Payable Subcontractor",
                "Payable Recruiter",
                "Gross Profit",
            ],
        ];

        const rowList = filteredReport.map((item) => [
            item.candidateId,
            `${item.candidateName} ${item.candidateLastName || ""}`.trim(),
            item.candidateType,
            item.subcontractor,
            item.recruiter,
            "$" + formatAmount(item.agencyAmount),
            "$" + formatAmount(item.amountReceived),
            "$" + formatAmount(item.candidateAmount),
            "$" + formatAmount(item.subonctractorAmount),
            "$" + formatAmount(item.recruiterAmount),
            "$" + formatAmount(item.grossProfit),
        ]);

        // add totals row
        rowList.push([
            "Total",
            "",
            "",
            "",
            "",
            "$" + formatAmount(totals.invoice),
            "$" + formatAmount(totals.received),
            "$" + formatAmount(totals.candidate),
            "$" + formatAmount(totals.subcontractor),
            "$" + formatAmount(totals.recruiter),
            "$" + formatAmount(totals.gross),
        ]);

        setHeaders(headerList);
        setRows(rowList);
        setExcelShowHide(true);
    };

    const handlepdfShowHide = () => {
        setPdfShowHide(false);
        setHeaders([]);
        setRows([]);
    };

    const handleexcelShowHide = () => {
        setExcelShowHide(false);
        setHeaders([]);
        setRows([]);
    };

    // Scroll synchronization
    const handleTopScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            tableWrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };

    const handleTableScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            topScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
        }
    };

    useEffect(() => {
        const updateWidth = () => {
            const table = document.getElementById("gross_profit_table");
            const wrapper = tableWrapperRef.current;
            if (table && wrapper) {
                setTableWidth(table.scrollWidth);
                setIsScrollable(table.scrollWidth > wrapper.clientWidth + 5);
            } else {
                setIsScrollable(false);
            }
        };

        updateWidth();
        const timer = setTimeout(updateWidth, 300);

        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            clearTimeout(timer);
        };
    }, [filteredReport, year, month]);

    return (
        <div className="ach-pay-container">
            <style>{`
                .ach-pay-container {
                    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    color: #1e293b;
                    margin-bottom: 2rem;
                }

                /* Visual selector cards */
                .ach-pay-card-selector {
                    margin-bottom: 12px;
                }

                .ach-pay-select-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    padding: 13px 16px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    height: 100%;
                    position: relative;
                }

                .ach-pay-select-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px -4px rgba(15, 23, 42, 0.06);
                    border-color: #cbd5e1;
                }

                .ach-pay-select-card.active {
                    background: #ffffff !important;
                    border: 1.5px solid #171717 !important;
                    box-shadow: 0 6px 18px -4px rgba(15, 23, 42, 0.08), inset 4px 0 0 0 #171717 !important;
                }

                .ach-pay-select-card.active .ach-pay-select-card-title {
                    color: #1e293b !important;
                    font-weight: 500 !important;
                }

                .ach-pay-select-card.active .ach-pay-select-card-desc {
                    color: #525252 !important;
                }

                .ach-pay-select-card-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 0 !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    flex-shrink: 0;
                    background: #f5f5f5;
                    color: #171717;
                    border: 1px solid #d4d4d4;
                    transition: all 0.2s ease;
                }

                .ach-pay-select-card.active .ach-pay-select-card-icon {
                    background: #171717 !important;
                    color: #ffffff !important;
                    border-color: #171717 !important;
                }

                .ach-pay-card-check-badge {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    width: 18px;
                    height: 18px;
                    border-radius: 0 !important;
                    background: #171717;
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    animation: badgePop 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                @keyframes badgePop {
                    from { transform: scale(0.6); opacity: 0; }
                    to   { transform: scale(1); opacity: 1; }
                }

                .ach-pay-select-card:hover .ach-pay-select-card-icon {
                    transform: scale(1.05);
                }

                .ach-pay-select-card-title {
                    font-size: 14px;
                    font-weight: 500;
                    color: #1e293b;
                    margin-bottom: 2px;
                }

                .ach-pay-select-card-desc {
                    font-size: 12px;
                    font-weight: 400;
                    color: #64748b;
                    line-height: 1.3;
                }

                /* KPI Metric cards */
                .ach-pay-kpi-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 14px;
                    margin-bottom: 12px;
                }

                .ach-pay-metric-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.01);
                }

                .ach-pay-metric-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 0 !important;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                    margin-right: 12px;
                    background: #f5f5f5;
                    color: #171717;
                    border: 1px solid #d4d4d4;
                    flex-shrink: 0;
                }

                .ach-pay-metric-val {
                    font-size: 18px;
                    font-weight: 500;
                    color: #1e293b;
                    line-height: 1.1;
                    margin-bottom: 2px;
                }

                .ach-pay-metric-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }

                /* Controls Layout */
                .ach-pay-controls {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-bottom: 12px;
                    background-color: #fafbfc;
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    padding: 12px 16px;
                }

                .ach-pay-controls-group {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .ach-pay-search-wrapper {
                    position: relative;
                    width: 240px;
                }

                .ach-pay-search-input {
                    width: 100%;
                    padding: 8px 12px 8px 34px;
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 0 !important;
                    font-size: 14px;
                    font-weight: 400;
                    color: #1e293b;
                    transition: all 0.2s ease;
                
                    height: 35px !important;
                    box-sizing: border-box !important;
                }

                .ach-pay-search-input:focus {
                    outline: none;
                    border-color: #171717;
                    box-shadow: 0 0 0 3px rgba(23, 23, 23, 0.1);
                }

                .ach-pay-search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-size: 12px;
                }

                .ach-pay-filter-select {
                    height: 35px;
                    border: 1px solid #cbd5e1;
                    border-radius: 0 !important;
                    padding: 0 10px;
                    font-size: 14px;
                    font-weight: 400;
                    color: #1e293b;
                    background: #ffffff;
                }

                .ach-pay-filter-select:focus {
                    outline: none;
                    border-color: #171717;
                }

                .ach-pay-actions-btns {
                    display: flex;
                    align-items: center;
                }

                .ach-pay-actions-btns > * {
                    margin-right: 8px !important;
                }

                .ach-pay-actions-btns > *:last-child {
                    margin-right: 0 !important;
                }

                .ach-pay-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 7px 14px;
                    height: 35px !important;
                    min-height: 35px !important;
                    box-sizing: border-box !important;
                    border-radius: 0 !important;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    color: #1e293b;
                    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
                }

                .ach-pay-btn:hover:not(:disabled) {
                    background-color: #f5f5f5;
                    color: #1e293b;
                    border-color: #94a3b8;
                }

                .ach-pay-btn:disabled {
                    background-color: #ffffff !important;
                    border-color: #cbd5e1 !important;
                    color: #94a3b8 !important;
                    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
                    cursor: not-allowed;
                }

                .ach-pay-btn-primary {
                    background: #2563eb !important;
                    color: #ffffff !important;
                    border: 1px solid #2563eb !important;
                    box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important;
                    transition: all 0.15s ease;
                    padding: 7px 16px !important;
                    height: 35px !important;
                    min-height: 35px !important;
                    box-sizing: border-box !important;
                }

                .ach-pay-btn-primary:hover:not(:disabled) {
                    background: #1d4ed8 !important;
                    border-color: #1d4ed8 !important;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
                }

                .ach-pay-btn-primary:disabled {
                    background-color: #dbe2ea !important;
                    border-color: #cbd5e1 !important;
                    color: #94a3b8 !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                    opacity: 0.85 !important;
                }

                /* Table modifications matching WorkflowUser */
                .ach-pay-table-wrapper {
                    border: 1px solid #e2e8f0;
                    border-radius: 0 !important;
                    overflow-x: auto;
                    overflow-y: hidden;
                    background: white;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.01);
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar {
                    height: 6px !important;
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar-track {
                    background: #f1f5f9 !important;
                    border-radius: 0 !important;
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #94a3b8, #cbd5e1) !important;
                    border-radius: 0 !important;
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #64748b, #94a3b8) !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar,
                .ach-pay-report-container div::-webkit-scrollbar {
                    width: 6px !important;
                    height: 6px !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-track,
                .ach-pay-report-container div::-webkit-scrollbar-track {
                    background: transparent !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-thumb,
                .ach-pay-report-container div::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #94a3b8, #cbd5e1) !important;
                    border-radius: 0 !important;
                    transition: all 0.2s ease !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-thumb:hover,
                .ach-pay-report-container div::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #64748b, #94a3b8) !important;
                }

                .ach-pay-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                    font-weight: 400;
                    text-align: left;
                }

                .ach-pay-table thead {
                    background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
                }

                .ach-pay-table thead tr {
                    background: transparent !important;
                }

                .ach-pay-table th {
                    background: transparent !important;
                    background-color: transparent !important;
                    color: #64748b !important;
                    font-weight: 600 !important;
                    font-size: 12px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.6px !important;
                    padding: 13px 16px !important;
                    border-bottom: 2px solid #e2e8f0 !important;
                    border-top: none !important;
                    border-left: none !important;
                    border-right: none !important;
                    white-space: nowrap !important;
                }

                .ach-pay-table tbody tr {
                    transition: background 0.15s ease, box-shadow 0.15s ease !important;
                }

                .ach-pay-table tbody tr:hover {
                    background: linear-gradient(90deg, #f5f5f5 0%, #fafafa 100%) !important;
                    box-shadow: inset 3px 0 0 0 #171717 !important;
                }

                .ach-pay-table tbody tr:hover td {
                    color: #1e293b !important;
                }

                .ach-pay-table td {
                    padding: 13px 16px !important;
                    font-size: 14px !important;
                    font-weight: 400;
                    color: #1e293b !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                    vertical-align: middle !important;
                    transition: color 0.15s ease !important;
                    white-space: nowrap !important;
                }

                .ach-pay-table tbody tr:last-child td {
                    border-bottom: none !important;
                }

                /* Column alignments and highlights */
                .ach-pay-table td.col-rate {
                    color: #475569 !important;
                    font-weight: 500;
                    text-align: right !important;
                }

                .ach-pay-table td.col-amount {
                    color: #1e293b !important;
                    font-weight: 500;
                    background-color: rgba(245, 245, 245, 0.5);
                    text-align: right !important;
                }

                .col-monthly-val, .col-quarterly-val {
                    color: #475569 !important;
                    font-weight: 500;
                    text-align: right !important;
                }

                /* Table Cell Numeric Input Groups */
                .ach-cell-input-group {
                    width: 110px !important;
                    height: 30px !important;
                    border: 1px solid #cbd5e1 !important;
                    border-radius: 0 !important;
                    background: #ffffff !important;
                    display: flex !important;
                    align-items: center !important;
                    transition: all 0.15s ease !important;
                    overflow: hidden !important;
                }

                .ach-cell-input-group:focus-within {
                    border-color: #171717 !important;
                    box-shadow: 0 0 0 3px rgba(23, 23, 23, 0.1) !important;
                }

                .ach-cell-input-prefix {
                    background: transparent !important;
                    border: none !important;
                    color: #64748b !important;
                    font-size: 12px !important;
                    font-weight: 400 !important;
                    padding: 0 0 0 8px !important;
                    display: flex !important;
                    align-items: center !important;
                }

                .ach-cell-num-input {
                    border: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                    color: #1e293b !important;
                    font-weight: 400 !important;
                    font-size: 14px !important;
                    height: 28px !important;
                    padding: 0 8px 0 2px !important;
                    text-align: right !important;
                    width: 100% !important;
                }

                .ach-cell-num-input:focus {
                    outline: none !important;
                    box-shadow: none !important;
                    border: none !important;
                }

                .ach-cell-num-input::-webkit-inner-spin-button,
                .ach-cell-num-input::-webkit-outer-spin-button,
                .ach-pay-table input[type="number"]::-webkit-inner-spin-button,
                .ach-pay-table input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none !important;
                    margin: 0 !important;
                }

                .ach-cell-num-input[type="number"],
                .ach-pay-table input[type="number"] {
                    -moz-appearance: textfield !important;
                }

                /* Total Payment row layout */
                .ach-pay-total-row {
                    background-color: #f8fafc !important;
                    border-top: 2px solid #cbd5e1;
                    border-bottom: 2px solid #cbd5e1;
                }

                .ach-pay-total-row td {
                    color: #1e293b !important;
                    font-weight: 500 !important;
                    padding: 14px 16px !important;
                }

                .ach-pay-total-label {
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    font-size: 12px;
                    font-weight: 600;
                    color: #64748b !important;
                    text-align: right !important;
                }

                .ach-pay-total-value {
                    font-size: 14px;
                    color: #1e293b !important;
                    font-weight: 500 !important;
                    background-color: #f8fafc !important;
                    text-align: right !important;
                }

                /* Copy button */
                .ach-copy-btn {
                    margin-left: 8px;
                    color: #94a3b8;
                    cursor: pointer;
                    font-size: 12px;
                    transition: color 0.15s ease;
                }

                .ach-copy-btn:hover {
                    color: #1e293b;
                }

                /* Shimmer and Loading Animation */
                @keyframes shimmer {
                  0%   { background-position: -600px 0; }
                  100% { background-position: 600px 0; }
                }

                @keyframes loadingBar {
                  0%   { width: 0%; opacity: 1; }
                  70%  { width: 85%; opacity: 1; }
                  100% { width: 100%; opacity: 0; }
                }

                .wf-loading-bar {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 3px;
                  background: linear-gradient(90deg, #171717, #737373, #171717);
                  background-size: 200% 100%;
                  animation: loadingBar 1.6s ease-in-out infinite;
                  z-index: 10;
                }

                .skeleton-cell {
                  display: block;
                  height: 12px;
                  border-radius: 0 !important;
                  background: linear-gradient(
                    90deg,
                    #f1f5f9 25%,
                    #e2e8f0 50%,
                    #f1f5f9 75%
                  );
                  background-size: 600px 100%;
                  animation: shimmer 1.4s ease-in-out infinite;
                }

                .skeleton-cell.sk-short  { width: 40%; }
                .skeleton-cell.sk-medium { width: 65%; }
                .skeleton-cell.sk-long   { width: 85%; }

                .skeleton-row td {
                  padding: 14px 16px !important;
                }

                /* Enhanced empty state */
                .wf-empty-state {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 50px 24px;
                  text-align: center;
                  gap: 12px;
                }

                .wf-empty-icon {
                  width: 52px;
                  height: 52px;
                  border-radius: 0 !important;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 22px;
                  background: #f5f5f5;
                  border: 1px solid #d4d4d4;
                  color: #1e293b;
                  margin-bottom: 4px;
                }

                .wf-empty-title {
                  font-size: 14px;
                  font-weight: 500;
                  color: #1e293b;
                  margin: 0;
                  width: 100%;
                  text-align: center;
                }

                .wf-empty-desc {
                  font-size: 14px;
                  font-weight: 400;
                  color: #64748b;
                  margin: 0 auto;
                  line-height: 1.6;
                  text-align: center;
                  width: 100%;
                }

                .ach-pay-loading-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 14px;
                    font-weight: 400;
                    color: #64748b;
                    margin-right: 10px;
                }

                .ach-pay-loading-status i {
                    color: #1e293b;
                }

            `}</style>

            <div className="ach-pay-controls">
                <div className="ach-pay-controls-group">
                    <div className="ach-pay-search-wrapper">
                        <i className="fa fa-search ach-pay-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Search by candidate, subcontractor..."
                            className="ach-pay-search-input"
                            value={tableSearchTerm}
                            onChange={(e) => setTableSearchTerm(e.target.value)}
                            disabled={monthlyGrossProfit.length === 0}
                        />
                    </div>

                    <SearchableSelect
                        id="year"
                        name="year"
                        size="sm"
                        width="130px"
                        icon="fa-calendar"
                        placeholder="Year..."
                        value={year}
                        onChange={handleYearChange}
                        options={yearOptions}
                    />

                    <SearchableSelect
                        id="month"
                        name="month"
                        size="sm"
                        width="140px"
                        icon="fa-calendar-o"
                        placeholder="Month..."
                        value={month}
                        onChange={handleMonthChange}
                        options={monthOptions}
                    />

                    <SearchableSelect
                        id="entity"
                        name="entity"
                        size="sm"
                        width="160px"
                        icon="fa-briefcase"
                        placeholder="All Entities"
                        value={selectedEntity}
                        onChange={(val) => setSelectedEntity(val || "")}
                        options={entityOptions}
                    />

                    <button
                        className="ach-pay-btn ach-pay-btn-primary"
                        disabled={!year || !month || isLoading}
                        onClick={GenerateGrossProfitRpt}
                    >
                        <i className={`fa ${isLoading ? "fa-spinner fa-spin" : "fa-refresh"}`} style={{ fontSize: "11px", marginRight: "6px" }}></i>
                            {isLoading ? "Loading..." : "Generate Report"}
                    </button>
                </div>

                <div className="ach-pay-actions-btns">
                    {isLoading && (
                            <div className="ach-pay-loading-status">
                                <i className="fa fa-spinner fa-spin"></i>
                                <span>Loading report...</span>
                            </div>
                        )}
                        <button
                            type="button"
                            className="ach-pay-btn"
                            disabled={isLoading || filteredReport.length === 0}
                            onClick={generateGrossProfitExcel}
                        >
                            <i className="fa fa-file-excel-o" style={{ color: "#1e293b", marginRight: "6px" }} aria-hidden="true"></i>
                            Excel Export
                        </button>

                    <button
                        className="ach-pay-btn"
                        disabled={isLoading || filteredReport.length === 0}
                        onClick={generateGrossProfitPdf}
                    >
                        <i className="fa fa-file-pdf-o" style={{ color: "#1e293b", marginRight: "6px" }}></i>
                        Export PDF
                    </button>
                </div>
            </div>

            {!year || !month ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "0", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-calendar-o"></i>
                    </div>
                    <p className="wf-empty-title">No Date Chosen</p>
                    <p className="wf-empty-desc">Please choose a valid Year and Month in the controls above.</p>
                </div>
            ) : !hasFetched ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "0", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-play-circle-o" style={{ color: "#4f46e5" }}></i>
                    </div>
                    <p className="wf-empty-title">Ready to Load Data</p>
                    <p className="wf-empty-desc">Click the "Generate Report" button above to fetch gross profits for this period.</p>
                </div>
            ) : (
                <div className="ach-pay-report-body p-0">
                    {/* KPI Metrics */}
                    {monthlyGrossProfit.length > 0 && (
                        <div className="ach-pay-kpi-row">
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-usd"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalInvoicedStr}</div>
                                    <div className="ach-pay-metric-label">Total Invoiced</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-check-circle-o"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalReceivedStr}</div>
                                    <div className="ach-pay-metric-label">Total Received</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-exclamation-triangle"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalPayablesStr}</div>
                                    <div className="ach-pay-metric-label">Total Payables</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-line-chart"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalGrossProfitStr}</div>
                                    <div className="ach-pay-metric-label">Net Gross Profit</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Synchronized top scrollbar */}
                    {monthlyGrossProfit.length > 0 && filteredReport.length > 0 && isScrollable && (
                        <div 
                            ref={topScrollRef}
                            onScroll={handleTopScroll}
                            className="ach-top-scrollbar-wrap"
                            style={{ overflowX: "auto", overflowY: "hidden", height: "8px", width: "100%", marginBottom: "2px" }}
                        >
                            <div style={{ width: `${tableWidth}px`, height: "1px" }}></div>
                        </div>
                    )}

                    <div 
                        ref={tableWrapperRef}
                        onScroll={handleTableScroll}
                        className="ach-pay-table-wrapper"
                        style={{ position: "relative" }}
                    >
                        {isLoading && <div className="wf-loading-bar" />}

                        {isLoading ? (
                            /* SKELETON TABLE LOADING SCREEN */
                            <table className="ach-pay-table">
                                <thead>
                                    <tr>
                                        <th colSpan={5}></th>
                                        <th colSpan={2} className="bg-white text-center">Accounts Receivable</th>
                                        <th colSpan={3} className="bg-lighter text-center">Accounts Payable</th>
                                        <th></th>
                                    </tr>
                                    <tr>
                                        <th>#</th>
                                        <th>Candidate</th>
                                        <th>Entity</th>
                                        <th>Subcontractor</th>
                                        <th>Recruiter</th>
                                        <th className="bg-white text-right">Amount Invoiced</th>
                                        <th className="bg-white text-right">Amount Received</th>
                                        <th className="bg-lighter text-right">Payable Candidate</th>
                                        <th className="bg-lighter text-right">Payable Subcontractor</th>
                                        <th className="bg-lighter text-right">Payable Recruiter</th>
                                        <th className="text-right">Gross Profit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <tr key={`skel-${i}`} className="skeleton-row">
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td className="bg-white"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td className="bg-white"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td className="bg-lighter"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td className="bg-lighter"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td className="bg-lighter"><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.09}s` }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : monthlyGrossProfit.length > 0 ? (
                            filteredReport.length > 0 ? (
                                <table
                                    className="ach-pay-table"
                                    id="gross_profit_table"
                                >
                                    <thead>
                                        <tr>
                                            <th colSpan={5}></th>
                                            <th colSpan={2} className="bg-white text-center" style={{ borderBottom: "2px solid #cbd5e1" }}>Accounts Receivable</th>
                                            <th colSpan={3} className="bg-lighter text-center" style={{ borderBottom: "2px solid #cbd5e1" }}>Accounts Payable</th>
                                            <th></th>
                                        </tr>

                                        <tr>
                                            <th>#</th>
                                            <th>Candidate</th>
                                            <th>Entity</th>
                                            <th>Subcontractor</th>
                                            <th>Recruiter</th>

                                            <th className="bg-white text-right">Amount Invoiced</th>
                                            <th className="bg-white text-right">Amount Received</th>

                                            <th className="bg-lighter text-right">Payable Candidate</th>
                                            <th className="bg-lighter text-right">Payable Subcontractor</th>
                                            <th className="bg-lighter text-right">Payable Recruiter</th>

                                            <th className="text-right">Gross Profit</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredReport.map((item) => (
                                            <tr key={item.candidateId}>
                                                <td>{item.candidateId}</td>
                                                <td>{item.candidateName} {item.candidateLastName}</td>
                                                <td>{item.candidateType}</td>
                                                <td>{item.subcontractor}</td>
                                                <td>{item.recruiter}</td>

                                                <td className="bg-white text-right col-amount">{"$" + formatAmount(item.agencyAmount)}</td>
                                                <td className="bg-white text-right col-amount">{"$" + formatAmount(item.amountReceived)}</td>
                                                <td className="bg-lighter text-right col-amount">{"$" + formatAmount(item.candidateAmount)}</td>
                                                <td className="bg-lighter text-right col-amount">{"$" + formatAmount(item.subonctractorAmount)}</td>
                                                <td className="bg-lighter text-right col-amount">{"$" + formatAmount(item.recruiterAmount)}</td>
                                                <td className="text-right col-amount">
                                                    {"$" + formatAmount(item.grossProfit)}
                                                </td>
                                            </tr>
                                        ))}

                                        {getTotalRow()}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="wf-empty-state">
                                    <div className="wf-empty-icon">
                                        <i className="fa fa-search"></i>
                                    </div>
                                    <p className="wf-empty-title">No Matching Records</p>
                                    <p className="wf-empty-desc">No entries matched your search query. Try adjusting your search term.</p>
                                </div>
                            )
                        ) : (
                            <div className="wf-empty-state">
                                <div className="wf-empty-icon">
                                    <i className="fa fa-folder-open-o"></i>
                                </div>
                                <p className="wf-empty-title">No Profits Found</p>
                                <p className="wf-empty-desc">No gross profit records found for this period selection.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* PDF Modal */}
            <GeneratePdf
                handlepdfShowHide={handlepdfShowHide}
                pdfShowHide={pdfShowHide}
                selectedCol={headers}
                selectedColData={rows}
            />
            <GenerateExcel
                handleexcelShowHide={handleexcelShowHide}
                excelShowHide={excelShowHide}
                selectedCol={headers}
                selectedColData={rows}
            ></GenerateExcel>
        </div>
    );
};

export default GrossProfitCandidates;